import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FaStarOfLife } from "react-icons/fa6";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Button from "../../components/shared/Button";
import {
  forgotPasswordPath,
  signUpPath,
  studentPath,
} from "../../components/routes/path";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api";

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: data.email,
        password: data.password,
      });

      if (response.data.success) {
        // Store token and user data
        localStorage.setItem("token", response.data.token);
        localStorage.setItem(
          "studentInfo",
          JSON.stringify(response.data.student)
        );

        // Show success toast
        toast.success("Login successful! Redirecting to dashboard...", {
          position: "top-right",
          autoClose: 2000,
        });

        // Delay navigation to allow toast to be visible
        setTimeout(() => {
          navigate(studentPath);
        }, 2000);
      }
    } catch (error) {
      // Handle error response from API
      const errorMessage =
        error.response?.data?.message || "Login failed. Please try again.";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center sm:max:h-[80vh] mb-10 px-2">
      <div className="bg-white shadow-2xl rounded-xl gap-7 p-10 flex flex-col w-[40rem]">
        <ToastContainer />
        <h2 className="font-bold text-2xl">LOGIN</h2>
        <p className="text-xs">Please, Log in to your account</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Email Field */}
          <div className="mb-5 space-y-2">
            <div className="flex gap-1">
              <label htmlFor="email" className="text-sm text-slate-500">
                <i>Email</i>
              </label>
              <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
            </div>
            <input
              type="email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              placeholder="Email"
              id="email"
              className="bg-[#cedce1] p-2 border-none rounded outline-none w-full"
            />
            {errors.email && (
              <p className="text-red-600 text-sm">{errors.email.message}</p>
            )}
          </div>

          {/* Password Field */}
          <div className="relative space-y-2">
            <div className="flex gap-1">
              <label htmlFor="password" className="text-sm text-slate-500">
                <i>Password</i>
              </label>
              <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
              placeholder="Password"
              id="password"
              className="bg-[#cedce1] p-2 rounded border-none outline-none w-full"
            />
            <div
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 bottom-3 cursor-pointer"
            >
              {showPassword ? (
                <FaEyeSlash className="text-sm" />
              ) : (
                <FaEye className="text-sm" />
              )}
            </div>
            {errors.password && (
              <p className="text-red-600 text-sm">{errors.password.message}</p>
            )}
          </div>

          {/* Remember Me */}
          <div className="mt-8 space-x-1">
            <input type="checkbox" id="remember" {...register("rememberMe")} />
            <label htmlFor="remember">Remember me</label>
          </div>

          {/* Submit Button */}
          <Button
            btnBgColor={
              isLoading
                ? "bg-white SA text-[#3b76db]"
                : "bg-[#3b76db] mt-3 text-white"
            }
            btnColor="shadow-lg"
            padding="py-3 w-full mt-5"
            btnText={isLoading ? "Logging in..." : "Login"}
            type="submit"
            disabled={isLoading}
          />
        </form>

        {/* Links */}
        <div className="text-center text-sm -mt-5">
          <Link to={forgotPasswordPath}>
            <p className="text-blue-600 hover:underline">
              Forgot your password?
            </p>
          </Link>
          <div className="flex justify-center gap-1 mt-2">
            <p>Not a Student?</p>
            <Link
              to={signUpPath}
              className="font-semibold text-blue-600 hover:underline"
            >
              Become a Student
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
